import { useMemo } from 'react';
import { useApp } from '../../../context/AppContext';
import { RATING_LABELS } from '../../../utils/trafficLight';

/**
 * Shared data hook for all Overview (Section 1) charts.
 * Call useOverviewData() from any chart in the overview/ folder
 * to read from the same memoized data — no prop threading needed.
 */
export function useOverviewData() {
  const { stats, filteredResults, trafficLights, reportType } = useApp();

  const areasOfWork = useMemo(() => {
    if (!trafficLights?.computed?.length) return [];
    return trafficLights.computed.map(aow => {
      const override = trafficLights.overrides?.[aow.name];
      const ratingKey = override ? override.rating : aow.rating;
      return {
        id: aow.name.replace(/\s+/g, '-').toLowerCase(),
        name: aow.name,
        rating: RATING_LABELS[ratingKey],
        ratingKey,
        progress: aow.progress,
        indicators: aow.indicators,
        hlos: aow.hlos || [],
        achievementPct: aow.achievementPct,
        resultCount: aow.resultCount,
      };
    }).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [trafficLights]);

  const w1w2Results = useMemo(
    () => filteredResults.filter(r => r.fundingType !== 'W3Bilateral'),
    [filteredResults]
  );
  const w3Results = useMemo(
    () => filteredResults.filter(r => r.fundingType === 'W3Bilateral'),
    [filteredResults]
  );

  return {
    stats,
    filteredResults,
    reportType,
    areasOfWork,
    w1w2Results,
    w3Results,
    byType: stats?.byType || null,
    byYear: stats?.byYear || null,
    byLevel: stats?.byLevel || null,
    byFundingType: stats?.byFundingType || null,
    byTypeAndFunding: stats?.byTypeAndFunding || null,
    byImpactArea: stats?.byImpactArea || null,
  };
}
